"use client";

import { motion } from "framer-motion";
import Link from "next/link";
import "./HoverGradientButton.css";

const MotionLink = motion.create(Link);

interface HoverGradientButtonProps {
  children: React.ReactNode;
  id?: string;
  className?: string;
  onClick?: () => void;
  href?: string;
  target?: string;
  rel?: string;
  variant?: 'filled' | 'outline';
  /** `sm` matches the admin toolbar pills (Filters / Columns). */
  size?: 'default' | 'sm';
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
  ariaLabel?: string;
  /** Marks the current item in a set of related controls (e.g. scroll-spy chips). */
  ariaCurrent?: boolean | 'true' | 'false' | 'page' | 'step' | 'location' | 'date' | 'time';
  /** Expand/collapse disclosure state for toggles that reveal more content. */
  ariaExpanded?: boolean;
  /** Pressed state for on/off toggles (e.g. add-to-routine). */
  ariaPressed?: boolean;
}

export default function HoverGradientButton({
  children,
  id = 'hover-gradient-button',
  className,
  onClick,
  href,
  target,
  rel,
  variant = 'filled',
  size = 'default',
  disabled = false,
  type = 'button',
  ariaLabel,
  ariaCurrent,
  ariaExpanded,
  ariaPressed,
}: HoverGradientButtonProps) {
  const handleClick = () => {
    if (disabled) return;
    onClick?.();
  };

  // Forward ARIA state so screen readers hear expand/press without reading only the label text.
  const motionProps = {
    className: `hgb-base rounded-full ${variant === 'filled' ? 'hgb-filled' : 'hgb-outline'}${size === 'sm' ? ' hgb-sm' : ''}${className ? ` ${className}` : ''}`,
    onClick: handleClick,
    'aria-label': ariaLabel,
    'aria-current': ariaCurrent,
    'aria-expanded': ariaExpanded,
    'aria-pressed': ariaPressed,
    whileHover: disabled ? {} : { scale: 1.05 },
    whileTap: disabled ? {} : { scale: 0.95 },
    transition: { type: "spring" as const, stiffness: 300, damping: 15 },
  };

  if (href) {
    return (
      <MotionLink id={id} href={href} target={target} rel={rel} {...motionProps}>
        {children}
      </MotionLink>
    );
  }

  return (
    // TODO: extract component
    <motion.button id={id} {...motionProps} disabled={disabled} type={type}>
      {children}
    </motion.button>
  );
}
